import '@vscode/codicons/dist/codicon.css';
import React from 'react';

import { Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';

import { JiraIssuesLogo } from '../home/JiraIssuesLogo';

const GILROY = '"Gilroy", ui-sans-serif, system-ui, sans-serif';
const PROXIMA = '"Proxima Nova", ui-sans-serif, system-ui, sans-serif';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `0.5px solid ${theme.palette.divider}`,
    margin: 0,
    color: theme.palette.text.primary,
  },
  headerTitle: {
    fontFamily: GILROY,
    fontSize: '1rem',
    fontWeight: 700,
  },
  personIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  button: {
    display: 'flex',
    flexDirection: 'column',
    borderRight: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 2),
    minHeight: 70,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    width: '100%',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    '& span': {
      fontSize: '1.5em',
      marginRight: theme.spacing(1.25),
      color: theme.palette.text.secondary,
    },
  },
  categoryText: {
    fontFamily: PROXIMA,
    fontWeight: 500,
    fontSize: '1em',
    color: theme.palette.text.primary,
    '& span': {
      fontSize: '0.9em',
      color: theme.palette.text.disabled,
    },
  },
  countText: {
    fontFamily: GILROY,
    fontSize: '1.5em',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}));

interface SummaryItem {
  id: string;
  category: string;
  count: number;
  icon: string | React.ReactNode;
}

interface WorkSummaryCardProps {
  onItemClick: (id: string) => void;
  issueCount: number;
  needsReviewsCount: number;
  returnedCount: number;
  waitingForAuthorCount: number;
  approvedCount: number;
  mergedCount: number;
  openPRsCount: number;
}

const WorkSummaryCard: React.FC<WorkSummaryCardProps> = React.memo(
  ({
    onItemClick,
    issueCount,
    needsReviewsCount,
    returnedCount,
    waitingForAuthorCount,
    approvedCount,
    mergedCount,
    openPRsCount,
  }) => {
    const classes = useStyles();

    const summaryData: SummaryItem[] = [
      {
        id: 'needs-review',
        category: 'Needs your review',
        count: needsReviewsCount,
        icon: <span className="codicon codicon-git-pull-request-done" />,
      },
      {
        id: 'returned',
        category: 'Returned to you',
        count: returnedCount,
        icon: (
          <span className="codicon codicon-git-pull-request-go-to-changes" />
        ),
      },
      {
        id: 'waiting-for-author',
        category: 'Waiting for Author',
        count: waitingForAuthorCount,
        icon: <span className="codicon codicon-git-pull-request-new-changes" />,
      },
      {
        id: 'approved',
        category: 'Approved',
        count: approvedCount,
        icon: <span className="codicon codicon-git-pull-request-done" />,
      },
      {
        id: 'open-prs',
        category: 'Waiting for Reviewers',
        count: openPRsCount,
        icon: <span className="codicon codicon-git-pull-request-create" />,
      },
      {
        id: 'merged',
        category: 'Recently Merged',
        count: mergedCount,
        icon: <span className="codicon codicon-git-merge" />,
      },
      {
        id: 'issues',
        category: 'Issues',
        count: issueCount,
        icon: <JiraIssuesLogo />,
      },
    ];

    const topRowItems = summaryData.slice(0, 2);
    const bottomRowItems = summaryData.slice(2);

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <PersonIcon className={classes.personIcon} />
          <Typography variant="subtitle1" className={classes.headerTitle}>
            My work
          </Typography>
        </div>
        <div className={classes.gridContainer}>
          {[...topRowItems, ...bottomRowItems].map(item => (
            <button
              key={item.id}
              onClick={() => onItemClick(item.id)}
              className={classes.button}
            >
              <div className={classes.iconWrapper}>
                <span>{item.icon}</span>
                <div>
                  <div className={classes.categoryText}>
                    {item.category} <span>&gt;</span>
                  </div>
                </div>
              </div>
              <div className={classes.countText}>{item.count}</div>
            </button>
          ))}
        </div>
      </div>
    );
  },
);

export default WorkSummaryCard;
